import type {
  ReploShopifyOption,
  ReploShopifyProduct,
} from "replo-runtime/shared/types";

import isString from "lodash-es/isString";

function isReploShopifyOption(value: unknown): value is ReploShopifyOption {
  return Boolean(
    value &&
      typeof value === "object" &&
      // Legacy cast - may or may not be necessary
      // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
      isString((value as Record<string, any>).key) &&
      // Legacy cast - may or may not be necessary
      // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
      isString((value as Record<string, any>).name) &&
      "values" in value
      ? Array.isArray(value.values)
      : true,
  );
}

export function isReploShopifyOptionList(
  value: unknown,
): value is ReploShopifyOption[] {
  return Array.isArray(value) && value.every(isReploShopifyOption);
}

/**
 * Check if an option value should be enabled given current partial selections.
 * Uses hierarchy: only considers selections from earlier options (option1 before option2, etc.)
 */
export function isOptionEnabled(
  productData: ReploShopifyProduct,
  selectedOptions: {
    option1?: string;
    option2?: string;
    option3?: string;
  },
  optionKey: "option1" | "option2" | "option3",
  optionValue: string,
): boolean {
  // Define option hierarchy
  const optionHierarchy = ["option1", "option2", "option3"];
  const currentOptionIndex = optionHierarchy.indexOf(optionKey);

  // Only consider selections from options that come before this one in hierarchy
  const upstreamOptions: Record<string, string> = {};
  for (let i = 0; i < currentOptionIndex; i++) {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const upstreamKey = optionHierarchy[i] as "option1" | "option2" | "option3";
    const upstreamValue = selectedOptions[upstreamKey];
    if (upstreamValue) {
      upstreamOptions[upstreamKey] = upstreamValue;
    }
  }

  // Add the proposed option value
  const testOptions = {
    ...upstreamOptions,
    [optionKey]: optionValue,
  };

  // Find variants that match this combination
  const matchingVariants = productData.variants.filter((variant) => {
    return Object.entries(testOptions).every(([key, value]) => {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      return String(variant[key as keyof typeof variant]) === String(value);
    });
  });

  // Enable if any matching variant is available
  return matchingVariants.some((variant) => variant.available);
}

/**
 * Get all option values with their availability status for a given option key
 */
export function getAvailableOptionValues(
  productData: ReploShopifyProduct,
  selectedOptions: {
    option1?: string;
    option2?: string;
    option3?: string;
  },
  optionKey: "option1" | "option2" | "option3",
): { title: string; available: boolean }[] {
  const option = productData.options.find((opt) => opt.key === optionKey);
  if (!option?.values) {
    return [];
  }

  return option.values.map((value) => ({
    title: value.title,
    available: isOptionEnabled(
      productData,
      selectedOptions,
      optionKey,
      value.title,
    ),
  }));
}
